<template>
  <div class="w-full min-h-[300px]">
    <div v-if="isLoadingOrder" class="flex justify-center py-12">
      <LayupSpinner />
    </div>

    <template v-else>
      <ContributeAmount
        v-if="step === 'amount'"
        :order="fullOrder"
        :upcoming="props.upcoming"
        @next="onAmountNext"
        @cancel="emit('close')"
      />

      <ContributeSelectPaymentMethod
        v-else-if="step === 'select-method'"
        :order="fullOrder"
        :amount-cents="amountCents"
        @select="onMethodSelect"
        @back="goBack"
      />

      <ContributePayment
        v-else-if="step === 'payment' && paymentPlanId && amountCents"
        :payment-plan-id="paymentPlanId"
        :amount-cents="amountCents"
        :payment-method="peachMethod"
        @completed="onPaymentCompleted"
        @cancelled="goBack"
        @back="goBack"
      />

      <ContributePayAt
        v-else-if="step === 'pay-at' && paymentPlanId"
        :payment-plan-id="paymentPlanId"
        :amount-cents="amountCents"
        :pay-at-reference="payAtReference"
        @back="goBack"
        @done="emit('close')"
      />

      <ContributeDebiCheck
        v-else-if="step === 'debi-check'"
        :order="fullOrder"
        :amount-cents="amountCents"
        @back="goBack"
      />

      <ContributePaymentStatus
        v-else-if="step === 'status' && currentPaymentId"
        :payment-id="currentPaymentId"
        @done="onStatusDone"
        @retry="onStatusRetry"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { useOrderStore } from '@/stores/orders';
import { computed, onMounted, ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';
import ContributeAmount from './ContributeAmount.vue';
import ContributeDebiCheck from './ContributeDebiCheck.vue';
import ContributePayAt from './ContributePayAt.vue';
import ContributePayment from './ContributePayment.vue';
import ContributePaymentStatus from './ContributePaymentStatus.vue';
import ContributeSelectPaymentMethod from './ContributeSelectPaymentMethod.vue';
import type { ContributePaymentMethod } from './ContributeSelectPaymentMethod.vue';

const props = defineProps<{
  order: ModelOrderResponse;
  upcoming?: { amount: number }[];
}>();

const fullOrder = ref<ModelOrderResponse>(props.order);
const isLoadingOrder = ref(false);

onMounted(async () => {
  if (!props.order.plans?.[0]?.payments && props.order._id) {
    isLoadingOrder.value = true;
    try {
      fullOrder.value = await getOrderByIdWithPlansAndPayments(props.order._id);
    } catch {
      // fall back to the original order
    } finally {
      isLoadingOrder.value = false;
    }
  }
});

const emit = defineEmits<{
  close: [];
  completed: [paymentId: string];
  'step-change': [step: Step];
}>();

type Step = 'amount' | 'select-method' | 'payment' | 'pay-at' | 'debi-check' | 'status';

const step = ref<Step>('amount');
const history = ref<Step[]>([]);
const amountCents = ref(0);
const selectedMethod = ref<ContributePaymentMethod>('CARD');
const currentPaymentId = ref('');

const paymentPlanId = computed(() => fullOrder.value.plans?.[0]?._id ?? '');

const peachMethod = computed(() => selectedMethod.value as 'CARD' | 'EFT');

const payAtReference = computed(() => (fullOrder.value.plans?.[0] as any)?.payAtReferenceNumber ?? '');

const goTo = (s: Step) => {
  history.value.push(step.value);
  step.value = s;

  emit('step-change', s);
};

const goBack = () => {
  const prev = history.value.pop();

  if (prev) {
    step.value = prev;
    emit('step-change', prev);
  }
};

const onAmountNext = (cents: number) => {
  amountCents.value = cents;

  goTo('select-method');
};

const onMethodSelect = (method: ContributePaymentMethod) => {
  selectedMethod.value = method;

  if (method === 'CARD' || method === 'EFT') {
    goTo('payment');
  } else if (method === 'PAY_AT') {
    goTo('pay-at');
  } else if (method === 'DEBI_CHECK') {
    goTo('debi-check');
  }
};

const onPaymentCompleted = (paymentId: string) => {
  currentPaymentId.value = paymentId;

  goTo('status');
};

const orderStore = useOrderStore();

const onStatusDone = async () => {
  if (fullOrder.value._id) {
    try {
      fullOrder.value = await getOrderByIdWithPlansAndPayments(fullOrder.value._id);
    } catch {
      // reload silently
    }
  }
  await orderStore.fetchAllOrders(true);
  emit('completed', currentPaymentId.value);
};

const onStatusRetry = () => {
  goTo('select-method');
};
</script>
